// El resumen de un pedido, como lo ve administracion.
//
// NO es el remito: el remito (src/app/remito.ts) es el documento que viaja con
// el reparto, sale en dos vias y esta hecho para imprimirse. Esto es para mirar
// en pantalla: que se pidio, cuanto de cada cosa y cuanto suma. Se usa cuando
// hay que decidir algo sobre el pedido —anularlo, por ejemplo— y no hace falta
// gastar papel.
//
// El del local es abrirDetalleLocal (pages/detallelocal.ts): mismo aspecto,
// pero pega contra /api/cliente/pedidos/:id, que solo deja ver los locales de
// la sesion. Este usa /api/pedidos/:id, que es de administracion.

import { api } from "./api";
import { formatearPesos } from "../lib/money";
import { nombreTurno } from "../lib/types";
import type { PedidoDetalleResp } from "./remito";

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

function ddmmaaaa(iso: string): string {
  if (!iso) return "";
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a}`;
}

/** Abre el resumen de un pedido en un <dialog>. Solo lectura. */
export async function abrirResumen(dlg: HTMLDialogElement, id: number): Promise<void> {
  dlg.innerHTML = `<div class="tarjeta w-[26rem] max-w-full border-0 shadow-none text-masa-400">Cargando el resumen...</div>`;
  dlg.showModal();

  const cerrar = (): void => {
    dlg.querySelector("#r_cerrar")?.addEventListener("click", () => dlg.close());
  };

  let r: PedidoDetalleResp;
  try {
    r = await api.get<PedidoDetalleResp>(`/api/pedidos/${id}`);
  } catch (e) {
    dlg.innerHTML = `
      <div class="tarjeta w-[24rem] max-w-full border-0 shadow-none">
        <p class="text-red-700">${esc(e instanceof Error ? e.message : "Error")}</p>
        <div class="mt-4 flex justify-end"><button id="r_cerrar" class="btn-primario">Cerrar</button></div>
      </div>`;
    cerrar();
    return;
  }

  const p = r.pedido;
  const filas = r.lineas
    .map(
      (l) => `<div class="flex items-center justify-between gap-2 border-t border-crema-100 py-2 text-sm">
        <span class="min-w-0 flex-1">${esc(l.descripcion)}</span>
        <span class="shrink-0 text-masa-500">${esc(l.cantidad)} ×</span>
        <span class="w-24 shrink-0 text-right tabular-nums text-masa-500">${formatearPesos(l.precio_unitario)}</span>
        <span class="w-24 shrink-0 text-right tabular-nums">${formatearPesos(l.subtotal)}</span>
      </div>`,
    )
    .join("");

  dlg.innerHTML = `
    <div class="tarjeta w-[30rem] max-w-full border-0 shadow-none">
      <h2 class="text-lg font-bold">Pedido #${esc(p.id)}</h2>
      <p class="mb-3 text-sm text-masa-500">
        ${esc(p.cliente_nombre)} · entrega ${ddmmaaaa(p.fecha_entrega)}${
          p.turno ? ` · ${esc(nombreTurno(p.turno))}` : ""
        }
      </p>
      <div class="max-h-[60vh] overflow-y-auto">
        ${filas || `<p class="py-2 text-sm text-masa-400">El pedido no tiene artículos.</p>`}
        ${
          p.descuento
            ? `<div class="flex items-center justify-between gap-2 border-t border-crema-100 py-2 text-sm">
                 <span>Descuento</span>
                 <span class="tabular-nums">−${formatearPesos(p.descuento)}</span>
               </div>`
            : ""
        }
        <div class="flex items-center justify-between gap-2 border-t-2 border-crema-200 py-2 font-bold">
          <span>Total</span>
          <span class="tabular-nums">${formatearPesos(p.total)}</span>
        </div>
      </div>
      <div class="mt-4 flex justify-end"><button id="r_cerrar" class="btn-primario">Cerrar</button></div>
    </div>`;
  cerrar();
}
